/**
 * Guided tours for the "Herramientas clave" checklist. Each tour is a short
 * sequence of steps: optionally navigate to a route, optionally spotlight an
 * element tagged with data-tour="<anchor>", and always explain — in one or two
 * sentences — what to do there. Keys match the checklist step keys in
 * primeros-pasos-steps.ts.
 *
 * PARITY: mobile renders these same instructions as a step-list sheet
 * (mobile/components/dashboard/PrimerosPasos.tsx) — keep the texts in sync.
 */

export interface TourStep {
  /** Navigate here when the step activates (skipped if already there). */
  route?: string;
  /** Spotlight the first element with data-tour="<anchor>", when present. */
  anchor?: string;
  title: string;
  body: string;
}

export const TOURS: Record<string, TourStep[]> = {
  publish: [
    {
      route: '/propiedades/nueva',
      title: 'Crea tu propiedad',
      body: 'Completa los datos básicos: título, tipo, operación, precio y ubicación. Puedes dictar o pegar una descripción y la IA rellena los campos por ti.',
    },
    {
      anchor: 'fotos',
      title: 'Sube las fotos',
      body: 'Arrastra o selecciona hasta 17 fotos. La primera es la portada — puedes reordenarlas y etiquetarlas (ej. "Cocina").',
    },
    {
      anchor: 'visibilidad',
      title: 'Activa Portales y guarda',
      body: 'En "Visibilidad y Red", activa Portales y pulsa Guardar. Tu propiedad quedará publicada en mapainmueble.com y los demás portales.',
    },
  ],
  share_prop: [
    {
      route: '/propiedades',
      anchor: 'share-toggle',
      title: 'Comparte a la Red de Intercambio',
      body: 'Cada tarjeta tiene el botón "Compartir". Púlsalo para que colegas de otras agencias vean tu propiedad y traigan compradores.',
    },
    {
      title: 'Recibe coincidencias',
      body: 'Al compartir, el sistema cruza tu propiedad con los requerimientos de toda la Red y te avisa cuando hay coincidencias.',
    },
  ],
  share_req: [
    {
      route: '/requerimientos',
      anchor: 'req-share-toggle',
      title: 'Comparte un requerimiento',
      body: 'Abre o crea un requerimiento de tu cliente y activa Intercambio en su tarjeta. Los colegas con propiedades que encajan lo verán.',
    },
    {
      title: 'Las coincidencias llegan solas',
      body: 'Cuando una propiedad compartida coincide con tu requerimiento, aparece en Coincidencias y recibes una notificación.',
    },
  ],
  vitrina: [
    {
      route: '/intercambio/propiedades',
      title: 'Explora el inventario de colegas',
      body: 'Estas son las propiedades que otras agencias compartieron a la Red. Puedes ofrecerlas a tus clientes y ganar comisión compartida.',
    },
    {
      anchor: 'property-card',
      title: 'Selecciona propiedades',
      body: 'Marca una o varias propiedades con el círculo de selección de cada tarjeta.',
    },
    {
      anchor: 'vitrina-whatsapp',
      title: 'Envíalas por WhatsApp',
      body: 'Pulsa "Enviar por WhatsApp": se crea una vitrina con tu nombre y tu contacto — el cliente ve las propiedades, tú recibes el interesado.',
    },
  ],
  contract: [
    {
      route: '/propiedades',
      anchor: 'property-card',
      title: 'Abre una propiedad tuya',
      body: 'Entra al detalle de la propiedad cuyo propietario debe autorizarte la venta o alquiler.',
    },
    {
      anchor: 'contrato',
      title: 'Envía el contrato',
      body: 'En la sección Propietario, pulsa "Enviar" en la fila Contrato. Le llega por WhatsApp y lo firma en línea desde su teléfono.',
    },
  ],
  report: [
    {
      route: '/propiedades',
      anchor: 'property-card',
      title: 'Abre una propiedad tuya',
      body: 'Entra al detalle de la propiedad de la que quieres rendir cuentas al propietario.',
    },
    {
      anchor: 'reporte-cliente',
      title: 'Abre el Reporte Cliente',
      body: 'Pulsa "Reporte Cliente" en la parte superior del detalle.',
    },
    {
      title: 'Comparte el enlace',
      body: 'Genera el enlace y envíaselo al propietario: verá visitas, contactos y la actividad de su propiedad — sin necesidad de cuenta.',
    },
  ],
};
